import { AuthSessionManager } from "@app/AuthSessionManager.ts";
import { trpcClient } from "@app/trpc.ts";
import { trpcUtils } from "@app/trpcUtils.ts";
import type { AuthSession } from "@server/models/auth.ts";
import { create } from "zustand/react";

type AuthUser = {
  id: string;
  email: string;
  session: AuthSession;
};

type AuthStore = {
  user: AuthUser | null;
  logout: () => void;
  load: () => Promise<AuthUser | null>;
  login: (auth: AuthUser) => void;
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  async load() {
    const snapshot = AuthSessionManager.getSnapshot();
    if (!snapshot.authToken && !snapshot.refreshToken) {
      set({ user: null });
      return null;
    }
    let data: Awaited<ReturnType<typeof trpcClient.auth.context.query>>;
    try {
      data = await trpcClient.auth.context.query();
    } catch (error) {
      if (!AuthSessionManager.isCurrent(snapshot)) {
        return get().user;
      }
      if (trpcUtils.isUnauthorized(error)) {
        get().logout();
        return null;
      }
      if (get().user) {
        return get().user;
      }
      throw error;
    }
    if (!AuthSessionManager.isCurrent(snapshot)) {
      return get().user;
    }
    const session = AuthSessionManager.getAuthSession();
    if (!session) {
      get().logout();
      return null;
    }

    const user = {
      id: data.userId,
      email: data.userEmail,
      session,
    };

    set({ user });

    return user;
  },
  logout() {
    AuthSessionManager.clear();
    set({ user: null });
  },
  login(user: AuthUser) {
    AuthSessionManager.save(user.session);
    set({ user });
  },
}));

AuthSessionManager.subscribe(() => useAuthStore.setState({ user: null }));
